import React, { useState, useEffect, useRef } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../firebaseConfig';

const OTPVerification = ({ 
  phoneNumber, 
  onVerificationSuccess, 
  onVerificationError, 
  onBack,
  isSignUp = false,
  userData = null 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const inputRefs = useRef([]);
  const recaptchaVerifier = useRef(null);

  // Initialize reCAPTCHA and send OTP
  useEffect(() => {
    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initializeRecaptcha();
    }, 100);

    return () => {
      clearTimeout(timer);
      // Cleanup reCAPTCHA verifier safely
      if (recaptchaVerifier.current && !recaptchaVerifier.current._destroyed) {
        try {
          recaptchaVerifier.current.clear();
        } catch (error) {
          console.warn('Error clearing reCAPTCHA verifier:', error);
        }
      }
      // Clear global reference
      if (window.recaptchaVerifier) {
        try {
          if (!window.recaptchaVerifier._destroyed) {
            window.recaptchaVerifier.clear();
          }
        } catch (error) {
          console.warn('Error clearing global reCAPTCHA verifier:', error);
        }
        delete window.recaptchaVerifier;
      }
    };
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const initializeRecaptcha = async () => {
    try {
      // Clear any existing reCAPTCHA verifier
      if (window.recaptchaVerifier) {
        try {
          if (!window.recaptchaVerifier._destroyed) {
            window.recaptchaVerifier.clear();
          }
        } catch (e) {
          // Ignore errors when clearing
        }
        delete window.recaptchaVerifier;
      }

      // Ensure the container exists
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        throw new Error('reCAPTCHA container not found');
      }

      // Create new reCAPTCHA verifier
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved, allow signInWithPhoneNumber
        },
        'expired-callback': () => {
          setError('reCAPTCHA expired. Please try again.');
        }
      });
      
      recaptchaVerifier.current = window.recaptchaVerifier;
      await sendOTP();
    } catch (error) {
      console.error('reCAPTCHA initialization error:', error);
      setError('Failed to initialize verification. Please refresh and try again.');
    }
  };

  const sendOTP = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Sending OTP to:', phoneNumber);
      console.log('reCAPTCHA verifier:', recaptchaVerifier.current);
      
      const appVerifier = recaptchaVerifier.current;
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setVerificationId(confirmationResult.verificationId);
      
      console.log('OTP sent successfully to:', phoneNumber);
      console.log('Verification ID:', confirmationResult.verificationId);
    } catch (error) {
      console.error('Error sending OTP:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        phoneNumber: phoneNumber
      });
      handleOTPError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPError = (error) => {
    let errorMessage = 'An error occurred while sending OTP';
    
    switch (error.code) {
      case 'auth/invalid-phone-number':
        errorMessage = 'Invalid phone number format';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many requests. Please try again later';
        break;
      case 'auth/quota-exceeded':
        errorMessage = 'SMS quota exceeded. Please try again later';
        break;
      case 'auth/captcha-check-failed':
        errorMessage = 'reCAPTCHA verification failed. Please try again';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    setError(errorMessage);
    onVerificationError && onVerificationError(errorMessage);
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    setError('');
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const verifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const credential = PhoneAuthProvider.credential(verificationId, otpString);
      const userCredential = await signInWithCredential(auth, credential);
      
      console.log('Phone authentication successful:', userCredential.user);
      
      // If it's signup, we might need to update user profile
      if (isSignUp && userData) {
        // Update user profile with additional data
        await userCredential.user.updateProfile({
          displayName: userData.name
        });
      }
      
      onVerificationSuccess && onVerificationSuccess(userCredential.user);
      
    } catch (error) {
      console.error('OTP verification error:', error);
      handleVerificationError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationError = (error) => {
    let errorMessage = 'Invalid OTP. Please try again';
    
    switch (error.code) {
      case 'auth/invalid-verification-code':
        errorMessage = 'Invalid OTP. Please check and try again';
        break;
      case 'auth/code-expired':
        errorMessage = 'OTP has expired. Please request a new one';
        break;
      case 'auth/session-expired':
        errorMessage = 'Session expired. Please start over';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    setError(errorMessage);
    onVerificationError && onVerificationError(errorMessage);
  };

  const resendOTP = async () => {
    if (!canResend) return;
    
    try {
      setResendLoading(true);
      setError('');
      setCountdown(60);
      setCanResend(false);
      
      // Reinitialize reCAPTCHA for resend
      await initializeRecaptcha();
      console.log('OTP resent successfully');
    } catch (error) {
      console.error('Error resending OTP:', error);
      handleOTPError(error);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* reCAPTCHA container - hidden but required for Firebase */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
      
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-white text-[20px] font-semibold mb-2" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Verify Phone Number
        </h2>
        <p className="text-[#CCCCCC] text-sm">
          We've sent a 6-digit code to <br />
          <span className="text-white font-medium">{phoneNumber?.startsWith('+91') ? phoneNumber : `+91 ${phoneNumber}`}</span>
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* OTP Input Fields */}
      <div className="flex justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength="1"
            value={digit}
            onChange={(e) => handleOTPChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={`w-10 h-10 sm:w-12 sm:h-12 text-center text-base sm:text-lg font-semibold bg-transparent text-white focus:outline-none border-b-2 transition-colors ${
              error ? 'border-red-500' : digit ? 'border-[#17BD3B]' : 'border-[#727274]'
            }`}
            disabled={loading}
          />
        ))}
      </div>

      {/* Verify Button */}
      <button
        onClick={verifyOTP}
        disabled={loading || otp.join('').length !== 6}
        className={`w-full py-[12px] px-[16px] rounded-[8px] border border-[#727274] font-semibold text-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#17BD3B]/50 focus:border-[rgba(120,248,148,0.70)] ring-offset-2 ring-offset-black mb-4 ${
          loading || otp.join('').length !== 6
            ? 'bg-gray-600 text-gray-300 cursor-not-allowed' 
            : 'bg-[#17BD3B] text-black hover:bg-[#15a834]'
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin mr-3"></div>
            Verifying...
          </div>
        ) : (
          'Verify OTP'
        )}
      </button>

      {/* Resend OTP */}
      <div className="text-center mb-6">
        {canResend ? (
          <button
            onClick={resendOTP}
            disabled={resendLoading}
            className="text-[#17BD3B] hover:underline text-sm disabled:opacity-50"
          >
            {resendLoading ? 'Sending...' : 'Resend OTP'}
          </button>
        ) : (
          <p className="text-[#CCCCCC] text-sm">
            Resend OTP in {countdown}s
          </p>
        )}
      </div>

      {/* Back Button */}
      <div className="text-center">
        <button
          onClick={onBack}
          disabled={loading}
          className="text-[#CCCCCC] hover:text-white text-sm transition-colors disabled:opacity-50"
        >
          ← Back to {isSignUp ? 'Sign Up' : 'Login'}
        </button>
      </div>
    </div>
  );
};

export default OTPVerification;
